import {
  CustomerContextAttributes,
  LanguageType,
  LocationAttributes,
  LocationType,
} from 'api-client'
import {
  DataTypePermission,
  DataTypeUser,
  LangCode,
  PermissionAttributes,
  PermissionResource,
  PhoneType,
  RoleType,
  SingleIncludedUserResourcePermissionResource,
  UserAttributes,
  UserStatus,
  UserType,
} from 'api-user-client'
import { innerJoin, pick, sortBy, uniqBy } from 'ramda'

import { SalesGroup } from '~shared-components/api/sales-groups'
import { loadSelectedSalesGroupId } from '~shared-components/redux/localStorage'
import { deserialize } from '~shared-components/utils/deserialize'
import { SupportedLanguage } from '~shared-components/utils/enums'
import { Unarray, WithId } from '~shared-components/utils/types'

import { UserContext } from '../model/location.model'
import { ifValue } from '../utils/functional'

export interface ValidatedCustomerModel extends CustomerContextAttributes {
  id: string
  locations: WithId<LocationAttributes>[]
  salesGroup: SalesGroup
}

export interface ExpandedUserResource extends UserAttributes {
  id: string
  permissions: WithId<PermissionAttributes>[]
}

export interface TransformedUser
  extends Omit<
    ExpandedUserResource,
    'createdAt' | 'lastLoggedInAt' | 'lastPasswordResetAt' | 'permissions'
  > {
  createdAt?: Date | undefined
  hasAcceptedTerms: boolean
  lastLoggedInAt?: Date | undefined
  lastPasswordResetAt?: Date | undefined
  permissions: UserPermission[]
  status: UserStatus
}

export type UserPermission = Pick<
  WithId<PermissionAttributes>,
  'customerContextId' | 'id' | 'role' | 'salesGroupId'
>
export interface EditableUserPermission extends UserPermission {
  customer?: CustomerContext
  isSelected?: boolean
}
export interface SalesGroupWithCustomers extends SalesGroup {
  customers: CustomerContext[] | undefined
}

const __symbolCurrentCustomerContext = Symbol.for('CurrentCustomeContextId')
const __symbolCurrentSalesGroupId = Symbol.for('CurrentSalesGroupId')
export interface ApplicationUser extends Omit<TransformedUser, 'language'> {
  [__symbolCurrentCustomerContext]: string | undefined
  [__symbolCurrentSalesGroupId]: string | undefined
  canCreateUser: () => boolean
  canEditUser: (user: ApplicationUser) => boolean
  currentCustomer?: CustomerContext
  currentCustomerContextId: string | undefined
  currentSalesGroup?: SalesGroup
  currentSalesGroupId: string | undefined
  customers?: CustomerContext[]
  internal: boolean
  isRole: (role: RoleType) => boolean
  isRoleAbove(role: RoleType, orEqual?: boolean): boolean
  isRoleAny: (role: RoleType, ...roles: RoleType[]) => boolean
  language?: SupportedLanguage
  permissions: UserPermission[] | EditableUserPermission[]
  primaryPhoneNumber: string
  role: RoleType
  salesGroupCountryCode: string | undefined
  salesGroupLanguages: LanguageType[]
  salesGroups?: SalesGroupWithCustomers[]
}

// Although SupportedLanguage is supposed to _always_ contain all languages in LangCode,
// this is to prevent accidental de-sync by throwing a type error if a new LangCode has been added
const langCodeMap: Record<LangCode, SupportedLanguage | undefined> = {
  en: SupportedLanguage.English,
  id: SupportedLanguage.Indonesian,
  ko: SupportedLanguage.Korean,
  th: SupportedLanguage.Thai,
  vi: SupportedLanguage.Vietnamese,
  'zh-CN': undefined,
  'zh-TW': SupportedLanguage.ChineseTaiwan,
}

export const convertLangCodeToSupportedLanguage = (
  language: LangCode | undefined,
): SupportedLanguage | undefined => langCodeMap[language as LangCode]

export const convertSupportedLanguageToLangCode = (
  language: SupportedLanguage | undefined,
): LangCode | undefined => {
  const entry = Object.entries(langCodeMap).find(
    ([, value]) => value === language,
  )
  return entry && (entry[0] as LangCode)
}

// Only display languages we actually have translations for
export const filterUnsupportedLanguages = (
  languages: LanguageType[] | undefined = [{ code: 'en', language: 'English' }],
): LanguageType[] =>
  (languages || [{ code: 'en', language: 'English' }]).filter(({ code }) =>
    Object.values(SupportedLanguage).includes(code as SupportedLanguage),
  )

export const transformApiUserToUser = (
  user: ExpandedUserResource,
): TransformedUser => {
  return {
    ...user,
    createdAt: ifValue(user.createdAt, v => new Date(v)),
    hasAcceptedTerms: Boolean(user.hasAcceptedTerms),
    lastLoggedInAt: ifValue(user.lastLoggedInAt, v => new Date(v)),
    lastPasswordResetAt: ifValue(user.lastPasswordResetAt, v => new Date(v)),
    permissions: user.permissions.map(
      ({ customerContextId, id, role, salesGroupId }) => ({
        customerContextId,
        id,
        role,
        salesGroupId,
      }),
    ),
    status: user.status || UserStatus.Active,
  }
}

export const convertApiPermissionsToUserPermissions = (
  apiPermission: PermissionResource[],
) =>
  apiPermission.map(({ attributes, id }) => ({
    customerContextId: attributes.customerContextId,
    id,
    role: attributes.role,
    salesGroupId: attributes.salesGroupId,
  }))

export const toCustomerContext = (ctx: ValidatedCustomerModel) => {
  const locations = ctx.locations
    .filter(location => location.isActive)
    .map(location => ({
      ...location,
      customerContext: {} as UserContext,
      id: location.id,
      type: location.type,
    }))

  return {
    billToAddresses: sortBy(
      item => item.name,
      locations.filter(l => l.type === LocationType.BillTo),
    ),
    customerContextId: ctx.id,
    customerName: ctx.customerName,
    customerNumber: ctx.customerNumber,
    id: ctx.id,
    languages: ctx.salesGroup.languages,
    locations,
    paymentTerms: ctx.paymentTerms ?? '',
    role: RoleType.User,
    salesGroup: {
      ...ctx.salesGroup,
    },
    shipToAddresses: sortBy(
      item => item.name,
      locations.filter(l => l.type === LocationType.ShipTo),
    ),
    // There can only be one sold-to address for a customer context
    soldToAddress: locations.find(l => l.type === LocationType.SoldTo),
  }
}
export const convertToCustomerContext = (
  userPermissions: UserPermission[],
  customerContexts: ValidatedCustomerModel[],
) => {
  const filteredCustomerContexts = innerJoin(
    (c, p) => p.customerContextId === c.id,
    customerContexts,
    userPermissions,
  )

  const defaultUserType = userPermissions.some(
    p => p.role === RoleType.UserManager,
  )
    ? RoleType.UserManager
    : RoleType.User

  return filteredCustomerContexts.map((ctx: ValidatedCustomerModel) => {
    const role =
      userPermissions.find(p => p.customerContextId === ctx.id)?.role ??
      defaultUserType

    return {
      ...toCustomerContext(ctx),
      role,
    }
  })
}

export const convertTransformedUserToApplicationUser = (
  user: TransformedUser,
  customers: CustomerContext[] | undefined = [],
  salesGroups?: SalesGroup[],
  currentSalesGroupId?: string,
): ApplicationUser => {
  const {
    email,
    language,
    // Because a phone number is mandatory, a new blank phone number
    // will be inserted for every user not having it coming from the backend.
    phoneNumbers = [
      {
        isPrimary: true,
        number: '',
        type: PhoneType.Mobile,
      },
    ],
  } = user

  const defaultCustomer = customers?.[0]
  const salesGroupId =
    currentSalesGroupId ??
    loadSelectedSalesGroupId(email) ??
    defaultCustomer?.salesGroup.id ??
    salesGroups?.[0]?.id
  const currentCustomer =
    customers.find(p => p.salesGroup.id === salesGroupId) ?? defaultCustomer
  const customerContextId = currentCustomer?.customerContextId
  const customerSalesGroups: SalesGroup[] =
    salesGroups ??
    uniqBy(
      (group: SalesGroup) => group.id,
      customers.map(customer => customer.salesGroup),
    )
  const rolesHierarchy = [
    RoleType.ReadUser,
    RoleType.User,
    RoleType.UserManager,
    RoleType.UserAdmin,
    RoleType.Csr,
    RoleType.Admin,
    RoleType.SuperAdmin,
  ] as const

  const newUser: ApplicationUser = {
    ...user,
    [__symbolCurrentCustomerContext]: undefined as string | undefined,
    [__symbolCurrentSalesGroupId]: undefined as string | undefined,
    canCreateUser() {
      return this.isRoleAny(
        RoleType.UserAdmin,
        RoleType.Admin,
        RoleType.SuperAdmin,
      )
    },
    canEditUser(userToEdit: ApplicationUser) {
      //CSR should not be able to edit anyone - business rule.
      if (this.isRole(RoleType.Csr)) {
        return false
      }

      const highestEditorRole = (this
        .permissions as UserPermission[]).reduce<number>(
        (maxRole, permission) =>
          Math.max(rolesHierarchy.indexOf(permission.role), maxRole),
        0,
      )

      const rolesUserCannotEdit = rolesHierarchy.slice(highestEditorRole)

      const canEditRole = userToEdit.permissions.every(
        ({ role }) => !rolesUserCannotEdit.includes(role),
      )

      // UserAdmin can only edit users when user does not have customer contexts that UserAdmin is not in:
      if (this.isRole(RoleType.UserAdmin)) {
        const userContextIds = (this.permissions as UserPermission[]).map(
          ({ customerContextId }) => customerContextId,
        )

        return (
          canEditRole &&
          userToEdit.permissions.every(({ customerContextId }) =>
            userContextIds.includes(customerContextId),
          )
        )
      }

      return canEditRole
    },
    currentCustomer,
    set currentCustomerContextId(contextId: string | undefined) {
      this[__symbolCurrentCustomerContext] = contextId
      this.currentCustomer = this.customers?.find(
        c => c.customerContextId === contextId,
      )
      this.currentSalesGroupId = this.currentCustomer?.salesGroup.id
    },
    get currentCustomerContextId(): string | undefined {
      return this[__symbolCurrentCustomerContext]
    },
    currentSalesGroup: undefined as SalesGroup | undefined,
    set currentSalesGroupId(groupId: string | undefined) {
      this[__symbolCurrentSalesGroupId] = groupId
      this.currentSalesGroup = this.salesGroups?.find(g => g.id === groupId)
      this.salesGroupCountryCode = this.currentSalesGroup?.country?.code
      this.salesGroupLanguages = filterUnsupportedLanguages(
        this.currentSalesGroup?.languages,
      )
    },
    get currentSalesGroupId(): string | undefined {
      return this[__symbolCurrentSalesGroupId]
    },
    customers,
    get internal(): boolean {
      return this.type === UserType.Internal
    },
    isRole(role: RoleType): boolean {
      return this.role === role
    },
    isRoleAbove(role: RoleType, orEqual?: boolean): boolean {
      const userRoleIndex = rolesHierarchy.indexOf(this.role)
      const roleIndex = rolesHierarchy.indexOf(role)
      return orEqual ? userRoleIndex >= roleIndex : userRoleIndex > roleIndex
    },
    isRoleAny(role: RoleType, ...roles: RoleType[]): boolean {
      return roles.concat(role).includes(this.role)
    },
    language: convertLangCodeToSupportedLanguage(language),
    phoneNumbers,
    primaryPhoneNumber: phoneNumbers.find(n => n.isPrimary)?.number ?? '',
    get role(): RoleType {
      if (!this.internal) {
        return this.currentCustomer?.role || RoleType.ReadUser
      }

      if (this.permissions.some(p => p.role === RoleType.SuperAdmin)) {
        return RoleType.SuperAdmin
      }

      return (
        this.permissions.find(p => p.salesGroupId === this.currentSalesGroupId)
          ?.role || RoleType.Csr
      )
    },
    salesGroupCountryCode: undefined as string | undefined,
    salesGroupLanguages: [] as LanguageType[],
    salesGroups: customerSalesGroups.map(g => ({
      ...g,
      customers: customers.filter(c => c.salesGroup.id === g.id),
    })),
  }

  newUser.currentCustomerContextId = customerContextId
  newUser.currentSalesGroupId = salesGroupId
  return newUser
}

export const convertApiUserToApplicationUser = (
  userModel: SingleIncludedUserResourcePermissionResource,
  customerModels: ValidatedCustomerModel[],
  salesGroups?: SalesGroup[],
  currentSalesGroupId?: string,
): ApplicationUser => {
  const userPermissions: UserPermission[] = convertApiPermissionsToUserPermissions(
    userModel?.included?.filter(included => included.type === 'Permission') ??
      [],
  )

  const customers: CustomerContext[] | undefined =
    customerModels.length >= 1
      ? convertToCustomerContext(userPermissions, customerModels)
      : undefined
  const user = transformApiUserToUser(
    deserialize<ExpandedUserResource>(userModel),
  )

  return convertTransformedUserToApplicationUser(
    user,
    customers,
    salesGroups,
    currentSalesGroupId,
  )
}

export type CustomerContext = Unarray<
  ReturnType<typeof convertToCustomerContext>
>

export type CustomerLocation = Unarray<CustomerContext['locations']>

export const NEW_USER_ID = '#new#'

export const newUser = (): ApplicationUser => {
  return {
    [__symbolCurrentCustomerContext]: '',
    [__symbolCurrentSalesGroupId]: '',
    blockedFor: [],
    canCreateUser: () => false,
    canEditUser: () => false,
    createdAt: undefined,
    currentCustomerContextId: undefined,
    currentSalesGroupId: undefined,
    email: '',
    hasAcceptedTerms: false,
    // We need default ID to link user with the relationships (permissions) which are sent in the same request as create user itself
    iamUserId: NEW_USER_ID,
    id: NEW_USER_ID,
    internal: false,
    isBlocked: false,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    isRole(_role: RoleType) {
      return false
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    isRoleAbove(_role: RoleType, _orEqual?: boolean) {
      return false
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    isRoleAny(_role: RoleType, ..._roles: RoleType[]) {
      return false
    },

    language: SupportedLanguage.English,
    lastEmailBouncedAt: undefined,
    lastLoggedInAt: undefined,
    lastPasswordResetAt: undefined,
    name: '',
    notes: undefined,
    permissions: [],
    phoneNumbers: [
      {
        isPrimary: true,
        number: '',
        type: PhoneType.Mobile,
      },
    ],
    primaryPhoneNumber: '',
    role: RoleType.ReadUser,
    salesGroupCountryCode: '',
    salesGroupLanguages: [],
    secondaryEmail: undefined,
    sendEmail: undefined,
    status: UserStatus.Active,
    type: UserType.External,
    updatedAt: undefined,
  }
}

export const transformApplicationUserToApiUser = (
  user: ApplicationUser,
): SingleIncludedUserResourcePermissionResource => {
  return {
    data: {
      attributes: {
        // Not all fields need to be saved so we are picking very few ones
        ...pick(
          [
            'iamUserId', // iam user id we cannot really adjust but it is required by API
            'type',
            'name',
            'email',
            'status',
            'notes',
            'hasAcceptedTerms',
            'isBlocked',
            'phoneNumbers',
          ],
          user,
        ),
        language: convertSupportedLanguageToLangCode(user.language),
      },
      id: user.id,
      relationships: {
        permissions: {
          data: [
            ...user.permissions.map((p: UserPermission) => ({
              id: p.id,
              type: DataTypePermission.Permission,
            })),
          ],
        },
      },
      type: DataTypeUser.User,
    },
    included: [
      ...user.permissions.map((p: UserPermission) => ({
        attributes: {
          ...pick(['role', 'customerContextId', 'salesGroupId'], p),
        },
        id: p.id,
        relationships: {
          user: {
            data: {
              id: user.id,
              type: DataTypeUser.User,
            },
          },
        },
        type: DataTypePermission.Permission,
      })),
    ],
  }
}
